import type { Dayjs } from 'dayjs';
import type { ApiRentalOccupiedRange } from 'src/types/rental';

import dayjs from 'dayjs';

import { formatDay } from './date';

/**
 * An occupied booking window as dayjs bounds. `end === null` marks an open-ended
 * hold: the api returns `endDate: null` for a defect/dispute hold, meaning the car
 * is occupied from `start` onward until the dealer manually re-enables the listing.
 */
export interface OccupiedInterval {
  start: Dayjs;
  end: Dayjs | null;
}

/** Parse an api occupied range into dayjs bounds, preserving the open-ended (`null`) upper bound. */
export const toOccupiedInterval = (range: ApiRentalOccupiedRange): OccupiedInterval => ({
  start: dayjs(range.startDate),
  end: range.endDate ? dayjs(range.endDate) : null,
});

/**
 * Whether a single day's `[day, day+1)` interval falls inside any occupied window.
 * A closed window is end-exclusive `[start, end)`; an open-ended window (`end === null`)
 * blocks every day from its `start` onward.
 */
export const isDayOccupied = (day: Dayjs, ranges: OccupiedInterval[]): boolean =>
  ranges.some((r) => day.add(1, 'day').isAfter(r.start) && (r.end === null || day.isBefore(r.end)));

/**
 * Card label for a car that is busy right now, or `null` when it is free and the
 * card should stay plain.
 *
 * The api sends `rentedNow` / `rentedUntil` only on an occupied listing, and
 * `rentedUntil` is the exclusive end of the current occupancy window — i.e. the
 * first day the car is free again, exactly what the booking calendar reopens.
 * `rentedUntil: null` is an open-ended defect/dispute hold: no date can be promised.
 *
 * `dayOf` prints the day of that moment: the visitor's own by default, and whatever a
 * server-rendered feed passes (`useDayFormat`).
 */
export const rentedBadgeLabel = (
  rentedNow?: boolean,
  rentedUntil?: string | null,
  dayOf: (moment: string) => string = formatDay
): string | null => {
  if (!rentedNow) {
    return null;
  }

  if (!rentedUntil) {
    return 'Currently unavailable';
  }

  return `Rented · available from ${dayOf(rentedUntil)}`;
};

/**
 * Whether a requested `[requestedStart, requestedEnd)` window overlaps any occupied window.
 * A closed occupied window overlaps when `requestedStart < endDate && requestedEnd > startDate`;
 * an open-ended window (`endDate === null`) has no upper bound, so it conflicts with any
 * requested window that ends after its `startDate`.
 */
export const hasBookingConflict = (
  requestedStart: Dayjs,
  requestedEnd: Dayjs,
  ranges: ApiRentalOccupiedRange[]
): boolean =>
  ranges.some(
    (range) =>
      requestedEnd.isAfter(dayjs(range.startDate)) &&
      (range.endDate === null || requestedStart.isBefore(dayjs(range.endDate)))
  );

/**
 * The line under the booking calendar for a car whose return is not promised —
 * occupancy truth §5. An `endDate: null` window means the api cannot name the
 * day the car frees up: it is out with a renter (or, rarely, held after a damage
 * claim, in which case the listing is unpublished anyway and nobody sees this).
 *
 * The calendar always refused those days; it just refused them silently, which
 * reads as a broken picker rather than as a car that is not back yet.
 */
export const openEndedOccupancyNote = (ranges: ApiRentalOccupiedRange[]): string | null =>
  ranges.some((range) => range.endDate === null)
    ? 'This car is currently rented and its return date is not confirmed — the nearest dates are unavailable.'
    : null;

/** The availability verdict the api attaches to an extension quote (occupancy truth §4.5). */
export interface RentalExtensionAvailability {
  status: 'free' | 'blocked' | 'warning';
  blockedUntil?: string | null;
}

/**
 * Why an extension cannot be approved, in the renter's words — or `null` when
 * there is nothing to say.
 *
 * `warning` is deliberately silent here: it means the DEALER has his own block
 * on those days (service, personal use), which is his to move and his private
 * business. Only another live booking makes the extension impossible, and the
 * renter needs to know that before approving money for it.
 */
export const extensionBlockedNote = (availability?: RentalExtensionAvailability | null): string | null => {
  if (availability?.status !== 'blocked') {
    return null;
  }

  const freeAgain = availability.blockedUntil
    ? ` until ${formatDay(availability.blockedUntil)}`
    : '';

  return `These days are already booked on this car${freeAgain} — the dealer will not be able to approve the extension, so nothing is charged.`;
};
